import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Typography, useTheme } from '@mui/material';
import { findMacroForTypedWord, getMacroPlainPreview } from '../../utils/macroKeywords';

/**
 * Sugere uma macro enquanto o técnico digita a ação do ticket (item 42).
 *
 * Fluxo: digita uma palavra-chave -> sugestão flutuante ancorada no cursor ->
 * Tab ou clique -> confirmação -> o texto da macro SUBSTITUI TUDO na caixa.
 *
 * A substituição é destrutiva (apaga o que já estava escrito), por isso a
 * confirmação não é opcional.
 */
const MacroKeywordSuggest = ({ quillRef, value, macros, onApply, disabled }) => {
    const theme = useTheme();
    const [suggestion, setSuggestion] = useState(null);
    const [confirming, setConfirming] = useState(null);
    const suggestionRef = useRef(null);

    // Mantém a sugestão atual acessível ao listener nativo de teclado sem
    // precisar recriá-lo a cada tecla.
    useEffect(() => { suggestionRef.current = suggestion; }, [suggestion]);

    const closeSuggestion = useCallback(() => setSuggestion(null), []);

    // Recalcula a sugestão a cada mudança do texto, olhando a palavra sob o cursor.
    useEffect(() => {
        if (disabled || !Array.isArray(macros) || macros.length === 0) {
            setSuggestion(null);
            return;
        }
        const editor = quillRef?.current?.getEditor?.();
        if (!editor) return;

        const sel = editor.getSelection();
        if (!sel) { setSuggestion(null); return; }

        const textoAteCursor = editor.getText(0, sel.index);
        const match = /(\S+)$/.exec(textoAteCursor);
        const palavra = match ? match[1] : '';

        const macro = findMacroForTypedWord(macros, palavra);
        if (!macro) { setSuggestion(null); return; }

        let bounds;
        try {
            bounds = editor.getBounds(sel.index);
        } catch (_) {
            setSuggestion(null);
            return;
        }

        setSuggestion({
            macro,
            palavra,
            top: (bounds?.bottom ?? 0) + 4,
            left: bounds?.left ?? 0,
        });
    // O value entra como dependência para reagir à digitação; a seleção é lida
    // do editor no momento do cálculo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, macros, disabled]);

    const pedirConfirmacao = useCallback(() => {
        const atual = suggestionRef.current;
        if (!atual) return;
        setConfirming(atual.macro);
        setSuggestion(null);
    }, []);

    // Tab precisa ser capturado antes do Quill: o editor usa Tab para indentar,
    // e no capture phase o listener roda antes do handler dele.
    useEffect(() => {
        const editor = quillRef?.current?.getEditor?.();
        const root = editor?.root;
        if (!root) return undefined;

        const onKeyDown = (e) => {
            if (!suggestionRef.current) return;
            if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                pedirConfirmacao();
            } else if (e.key === 'Escape') {
                setSuggestion(null);
            }
        };

        root.addEventListener('keydown', onKeyDown, true);
        return () => root.removeEventListener('keydown', onKeyDown, true);
    }, [quillRef, pedirConfirmacao, value]);

    const confirmarAplicacao = () => {
        const macro = confirming;
        setConfirming(null);
        if (macro && onApply) onApply(macro);
    };

    return (
        <>
            {suggestion && (
                <Paper
                    elevation={6}
                    onMouseDown={(e) => { e.preventDefault(); pedirConfirmacao(); }}
                    sx={{
                        position: 'absolute',
                        top: suggestion.top,
                        left: suggestion.left,
                        zIndex: 1400,
                        px: 1,
                        py: 0.5,
                        maxWidth: 320,
                        cursor: 'pointer',
                        border: `1px solid ${theme.palette.primary.main}`,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: theme.palette.action.hover },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.primary.main, whiteSpace: 'nowrap' }}>
                            {suggestion.macro.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                            Tab para aplicar
                        </Typography>
                    </Box>
                </Paper>
            )}

            <Dialog open={!!confirming} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Aplicar macro "{confirming?.name}"?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        O texto desta macro vai <strong>substituir todo o conteúdo</strong> da caixa de ação.
                        O que você já escreveu será perdido.
                    </Typography>
                    <Box
                        sx={{
                            p: 1,
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.default,
                            maxHeight: 160,
                            overflow: 'auto',
                        }}
                    >
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'pre-wrap' }}>
                            {getMacroPlainPreview(confirming) || '(a macro não tem texto de mensagem)'}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(null)} color="inherit">Cancelar</Button>
                    <Button onClick={confirmarAplicacao} variant="contained">Substituir texto</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MacroKeywordSuggest;
